import { Box, Button, Text } from "@salestrak/ui";

import type { FilterBlock as FilterBlockType, FilterConfig, FilterValue } from "#app/lib/filters/types";
import { FilterRow } from "./filter-row";
import { LogicSelect } from "./logic-select";

type FilterBlockProps = {
  config: FilterConfig;
  block: FilterBlockType;
  onAddFilter: () => void;
  onRemoveFilter: (filterIndex: number) => void;
  onUpdateFilter: (filterIndex: number, update: Partial<FilterValue>) => void;
  onSetBlockLogic: (logic: "AND" | "OR") => void;
  onRemoveBlock: () => void;
};

/**
 * A single filter block containing N filter rows.
 *
 * Shows an internal AND/OR logic selector when there are multiple filters.
 * Renders in a card-like container with muted background.
 */
export function FilterBlock({
  config,
  block,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onSetBlockLogic,
  onRemoveBlock,
}: FilterBlockProps) {
  return (
    <Box
      border
      rounded="lg"
      paddingLeft="sm"
      paddingRight="md"
      paddingBottom="lg"
      paddingTop="sm"
    >
      <Box gap="md">
        {block.filters.map((filter, filterIndex) => (
          <Box key={filterIndex} gap="sm">
            <FilterRow
              config={config}
              filter={filter}
              leftLabel={
                filterIndex === 0 ? undefined : filterIndex === 1 ? (
                  <LogicSelect value={block.logic} onChange={onSetBlockLogic} />
                ) : (
                  <Text size="sm" variant="muted" weight="medium">
                    {block.logic}
                  </Text>
                )
              }
              onUpdate={(update) => onUpdateFilter(filterIndex, update)}
              onRemove={() => onRemoveFilter(filterIndex)}
            />
          </Box>
        ))}

        {/* Footer actions */}
        <Box paddingTop="xs">
          <Box direction="row" align="center" gap="sm" justify="between">
            <Button variant="ghost" size="sm" onClick={onAddFilter}>
              <Text weight="medium">Add nested filter</Text>
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemoveBlock}>
              <Text size="sm" variant="muted" weight="medium">Clear group</Text>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
